import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { Dropdown, Menu, message, Button } from 'antd';
import { LoadingSpinner } from './LoadingSpinner';
import PageItem from '../components/PageItem';
import { PageEmptyState } from './PageEmptyState';
import {
  SyncOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { analytics } from '../modules/analytics';


export class PageMap extends React.Component {
  static propTypes = {
    page: PropTypes.shape({
      pages: PropTypes.array.isRequired,
      status: PropTypes.string,
    }).isRequired,

    domain: PropTypes.shape({
      domains: PropTypes.array.isRequired,
      status: PropTypes.string,
    }).isRequired,

    getPages: PropTypes.func.isRequired,
    addPage: PropTypes.func.isRequired,
    addTutorialPage: PropTypes.func.isRequired,
    deletePage: PropTypes.func.isRequired,
    updatePageAttr: PropTypes.func.isRequired,
    checkPageSubdomainAvailability: PropTypes.func.isRequired,

    getDomains: PropTypes.func.isRequired,
    refreshDomains: PropTypes.func.isRequired,

    networkError: PropTypes.bool.isRequired,

    addPageVariant: PropTypes.func.isRequired,
    deletePageVariant: PropTypes.func.isRequired,
    clonePageVariant: PropTypes.func.isRequired,
    updatePageVariantAttr: PropTypes.func.isRequired,
    updatePageVariantDistribution: PropTypes.func.isRequired,
  };

  componentDidMount() {
    analytics.trackView('/pages');

    if (this.props.page.status === 'init') {
      this.props.getPages();
    }
    if (this.props.domain.status === 'init') {
      this.props.getDomains();
    }
  }

  componentDidUpdate(prevProps) {

    // refresh content upon recovery from network error
    if (prevProps.networkError && !this.props.networkError) {
      this.props.getPages();
      this.props.getDomains();
    }

    // clear "updating" message upon successful refresh
    const {page: prevPage, domain: prevDomain} = prevProps;
    const {page, domain} = this.props;
    if (
      (prevPage.status === 'refreshing' || prevDomain.status === 'refreshing')
      && (page.status !== 'refreshing' && domain.status !== 'refreshing')
    ) {
      message.success({
        content: 'Pages updated',
        duration: 1.5,
        key: 'REFRESH_MESSAGE_KEY',
      });
    }

  }

  isNotInDeletingMode = (x) => x.status !== 'deleting';

  onPageAdd = () => {
    // analytics.trackEvent('page', 'create');
    this.props.addPage();
  };

  onTutorialPageAdd = () => {
    // analytics.trackEvent({ category: 'page_tutorial', action: 'create' });
    this.props.addTutorialPage();
  }

  onPageDelete = (pageUrlsafe) => {
    // analytics.trackEvent({ category: 'page', action: 'delete' });
    this.props.deletePage(pageUrlsafe);
  };

  onPageAttributeChange = (pageUrlsafe, changes) => {
    this.props.updatePageAttr(pageUrlsafe, changes);
  };

  checkPageSubdomainAvailability = (pageUrlsafe, subdomain) => {
    this.props.checkPageSubdomainAvailability(pageUrlsafe, subdomain);
  };

  onPageVariantAdd = (pageUrlsafe) => {
    this.props.addPageVariant(pageUrlsafe);
  };

  onPageVariantDelete = (pageUrlsafe, variantUrlsafe) => {
    analytics.trackEvent({ category: 'pages_variant', action: 'delete', label: variantUrlsafe });
    this.props.deletePageVariant(pageUrlsafe, variantUrlsafe);
  };

  onPageVariantClone = (pageUrlsafe, variantUrlsafe) => {
    this.props.clonePageVariant(pageUrlsafe, variantUrlsafe);
  };

  onPageVariantAttributeChange = (pageUrlsafe, variantUrlsafe, changes) => {
    this.props.updatePageVariantAttr(pageUrlsafe, variantUrlsafe, changes);
  }

  onPageVariantDistributionChange = (pageUrlsafe, variantUrlsafe, value) => {
    this.props.updatePageVariantDistribution(pageUrlsafe, variantUrlsafe, value);
  }

  updatePageAttr = (pageUrlsafe, changes) => {
    this.props.updatePageAttr(pageUrlsafe, changes);
  };

  refreshBtnClicked = () => {
    if(this.props.page.status !== 'refreshing') {
      this.props.refreshPages();
    }
    if(this.props.domain.status !== 'refreshing') {
      this.props.refreshDomains();
    }
    message.loading({
      content: 'Updating ...',
      duration: 0,
      key: 'REFRESH_MESSAGE_KEY',
    });
  };

  renderHeaderBar = (pages) => {
    const numPages = pages.length;
    if (numPages === 0) {
      return null;
    }
    const pageStr = (pages.length > 1) ? 'pages' : 'page';

    return (
      <div className="d-flex flex-row justify-content-between mb-4">
        <div className="d-flex flex-row align-items-center">
          <div className="mr-3">
            <Dropdown.Button
              onClick={this.onPageAdd}
              overlay={
                <Menu onClick={e => {
                  const { key } = e;
                  if (key === 'add-tutorial-page') {
                    this.onTutorialPageAdd();
                  }
                }}>
                  <Menu.Item key="add-tutorial-page"><PlusOutlined />New tutorial page</Menu.Item>
                </Menu>
              }
              type="primary"
              placement="bottomLeft"
            >
              <PlusOutlined />
              New page
            </Dropdown.Button>
          </div>
          <div className="text-secondary">{numPages} {pageStr}</div>
        </div>
        <div className="d-flex flex-row">
          <Button onClick={this.refreshBtnClicked} icon={<SyncOutlined />} type="link" className="px-0">Refresh</Button>
        </div>
      </div>
    );

  };

  render() {
    const { pages, status } = this.props.page;
    const { domains } = this.props.domain;

    const isLoading = (status === 'loading');
    const isEmpty = (pages.length === 0);

    const emptyState = (
      <PageEmptyState
        onPageAdd={this.onPageAdd}
        onTutorialPageAdd={this.onTutorialPageAdd}
      />
    );

    const filteredPages = pages.filter(this.isNotInDeletingMode);
    const pageItems = filteredPages.map((page) => (
      <PageItem
        key={page.urlsafe}

        onPageDelete={this.onPageDelete}
        onPageAttributeChange={this.onPageAttributeChange}
        checkPageSubdomainAvailability={this.checkPageSubdomainAvailability}

        onPageVariantAdd={this.onPageVariantAdd}
        onPageVariantDelete={this.onPageVariantDelete}
        onPageVariantClone={this.onPageVariantClone}
        onPageVariantAttributeChange={this.onPageVariantAttributeChange}
        onPageVariantDistributionChange={this.onPageVariantDistributionChange}

        {...page}
        domains={domains}
      />
    ));

    let title = null;
    let pageMapContent = null;
    if (isLoading) {
      title = 'Loading';
      pageMapContent = <LoadingSpinner />;
    }
    else if (isEmpty) {
      title = 'Create your first landing page';
      pageMapContent = emptyState;
    }
    else {
      title = 'Landing pages';
      pageMapContent = (
        <React.Fragment>
          { this.renderHeaderBar(pages) }
          { pageItems }
        </React.Fragment>
      );
    }

    return (
      <div className="app__page">
        <Helmet>
          <title>{ title }</title>
        </Helmet>
        { pageMapContent }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/presentation/PageMap.jsx